import React, { useState, useEffect, useRef } from 'react';
import './zoomContainer.css';

const ZoomWrapper = ({ children, ref }) => {
  // We use `useState` for values that need to trigger a re-render for UI updates
  const [scale, setScale] = useState(1);
  const [translateX, setTranslateX] = useState(0);
  const [translateY, setTranslateY] = useState(0);

  // We use `useRef` for values that need to be mutable and accessed by event handlers
  // without triggering a re-render. This is the key to the fix.
  const scaleRef = useRef(1);
  const translateXRef = useRef(0);
  const translateYRef = useRef(0);
  const isDraggingRef = useRef(false);
  const lastMouseXRef = useRef(0);
  const lastMouseYRef = useRef(0);

  // Refs for the DOM elements
  const wrapperRef = useRef(null);
  const contentRef = useRef(null);

  // A memoized style object for the inner zoomable container
  const zoomableStyle = {
    transform: `translate(${translateX}px, ${translateY}px) scale(${scale})`,
    transformOrigin: '0 0',
    cursor: isDraggingRef.current ? 'grabbing' : 'grab',
  };

  // Centralized function to update state and refs
  const updateTransform = (newScale, newTranslateX, newTranslateY) => {
    setScale(newScale);
    setTranslateX(newTranslateX);
    setTranslateY(newTranslateY);
    scaleRef.current = newScale;
    translateXRef.current = newTranslateX;
    translateYRef.current = newTranslateY;
  };

  const zoom = (newScale, pointX, pointY) => {
    const oldScale = scaleRef.current;

    // Clamp the scale to a reasonable range
    newScale = Math.max(0.1, Math.min(newScale, 5));

    // Calculate the new translation to keep the point under the cursor
    const newTranslateX =
      pointX - (newScale / oldScale) * (pointX - translateXRef.current);
    const newTranslateY =
      pointY - (newScale / oldScale) * (pointY - translateYRef.current);

    updateTransform(newScale, newTranslateX, newTranslateY);
  };

  /**
   * Function to handle the auto-fit logic. It can be called from outside the component.
   */
  const resetZoom = () => {
    const wrapper = wrapperRef.current;
    const content = contentRef.current;

    if (wrapper && content) {
      const wrapperRect = wrapper.getBoundingClientRect();
      const contentRect = content.getBoundingClientRect();

      const scaleX = wrapperRect.width / contentRect.width;
      const scaleY = wrapperRect.height / contentRect.height;
      const initialScale = Math.min(scaleX, scaleY) * 0.9;

      const initialTranslateX =
        (wrapperRect.width - contentRect.width * initialScale) / 2;
      const initialTranslateY =
        (wrapperRect.height - contentRect.height * initialScale) / 2;
      if (isNaN(initialTranslateX) || isNaN(initialTranslateY)) {
        return;
      }
      updateTransform(initialScale, initialTranslateX, initialTranslateY);
    }
  };

  const zoomIn = () => {
    const rect = wrapperRef.current.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    zoom(scaleRef.current * 1.2, centerX, centerY);
  };

  const zoomOut = () => {
    const rect = wrapperRef.current.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    zoom(scaleRef.current / 1.2, centerX, centerY);
  };

  // Expose the functions to the parent component via the ref
  React.useImperativeHandle(ref, () => ({
    zoomIn: zoomIn,
    zoomOut: zoomOut,
    resetZoom: resetZoom,
  }));

  // This effect runs only once to set up all event listeners and the initial fit.
  useEffect(() => {
    // Initial fit on load, with a small delay to ensure all DOM elements are rendered
    const timeoutId = setTimeout(resetZoom, 100);

    const wrapperElement = wrapperRef.current;

    const handleWheel = (e) => {
      e.preventDefault();

      const rect = wrapperElement.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const oldScale = scaleRef.current;
      const zoomFactor = 1.1;
      let newScale =
        e.deltaY < 0 ? oldScale * zoomFactor : oldScale / zoomFactor;

      zoom(newScale, mouseX, mouseY);
    };

    const handleMouseDown = (e) => {
      if (e.button === 0) {
        isDraggingRef.current = true;
        lastMouseXRef.current = e.clientX - translateXRef.current;
        lastMouseYRef.current = e.clientY - translateYRef.current;
        wrapperElement.style.cursor = 'grabbing';
      }
    };

    // Add touch event listeners for mobile devices
    const handleTouchStart = (e) => {
      if (e.touches.length === 1) {
        isDraggingRef.current = true;
        lastMouseXRef.current = e.touches[0].clientX - translateXRef.current;
        lastMouseYRef.current = e.touches[0].clientY - translateYRef.current;
        wrapperElement.style.cursor = 'grabbing';
      }
    };

    const handleMouseMove = (e) => {
      if (!isDraggingRef.current) return;
      const newTranslateX = e.clientX - lastMouseXRef.current;
      const newTranslateY = e.clientY - lastMouseYRef.current;

      updateTransform(scaleRef.current, newTranslateX, newTranslateY);
    };

    const handleTouchMove = (e) => {
      if (!isDraggingRef.current || e.touches.length !== 1) return;
      const newTranslateX = e.touches[0].clientX - lastMouseXRef.current;
      const newTranslateY = e.touches[0].clientY - lastMouseYRef.current;

      updateTransform(scaleRef.current, newTranslateX, newTranslateY);
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
      wrapperElement.style.cursor = 'grab';
    };

    const handleTouchEnd = () => {
      isDraggingRef.current = false;
      wrapperElement.style.cursor = 'grab';
    };

    if (wrapperElement) {
      wrapperElement.addEventListener('wheel', handleWheel, { passive: false });
      wrapperElement.addEventListener('mousedown', handleMouseDown);
      wrapperElement.addEventListener('mousemove', handleMouseMove);
      wrapperElement.addEventListener('mouseup', handleMouseUp);
      wrapperElement.addEventListener('mouseleave', handleMouseUp);

      // Add touch listeners
      wrapperElement.addEventListener('touchstart', handleTouchStart, {
        passive: false,
      });
      wrapperElement.addEventListener('touchmove', handleTouchMove, {
        passive: false,
      });
      wrapperElement.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      clearTimeout(timeoutId);
      if (wrapperElement) {
        wrapperElement.removeEventListener('wheel', handleWheel);
        wrapperElement.removeEventListener('mousedown', handleMouseDown);
        wrapperElement.removeEventListener('mousemove', handleMouseMove);
        wrapperElement.removeEventListener('mouseup', handleMouseUp);
        wrapperElement.removeEventListener('mouseleave', handleMouseUp);

        // Cleanup touch listeners
        wrapperElement.removeEventListener('touchstart', handleTouchStart);
        wrapperElement.removeEventListener('touchmove', handleTouchMove);
        wrapperElement.removeEventListener('touchend', handleTouchEnd);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array ensures this effect runs only once

  return (
    <>
      {/* Control Panel with zoom and reset buttons */}
      <div className="buttonStyle">
        <button onClick={zoomIn}>{/* Zoom In Icon */}+</button>
        <button onClick={zoomOut}>{/* Zoom Out Icon */}-</button>
      </div>
      <div
        ref={wrapperRef}
        className="zoomableContainer"
        style={{ cursor: 'grab' }}
      >
        <div ref={contentRef} style={zoomableStyle}>
          <div className="relative">{children}</div>
        </div>
      </div>
    </>
  );
};

export default ZoomWrapper;
